import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    studentName: '',
    examYear: '2027', // Default to 2027 (2026 exam completed)
    examType: '' // No default — user must choose
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [activeStep, setActiveStep] = useState(1);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [emailError, setEmailError] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [redirectTimer, setRedirectTimer] = useState(null);

  // Countdown timer effect
  useEffect(() => {
    if (success && countdown > 0 && !isPaused) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (success && countdown === 0 && !isPaused) {
      navigate('/');
    }
  }, [success, countdown, navigate, isPaused]);

  // Clear redirect timer when paused
  useEffect(() => {
    if (isPaused && redirectTimer) {
      clearTimeout(redirectTimer);
      setRedirectTimer(null);
    }
  }, [isPaused, redirectTimer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Real-time password matching
    if (name === 'confirmPassword' || name === 'password') {
      if (name === 'confirmPassword') {
        setPasswordMatch(value === formData.password || value === '');
      } else if (name === 'password') {
        setPasswordMatch(formData.confirmPassword === value || formData.confirmPassword === '');
      }
    }
  };

  // Check email availability when user finishes typing
  const checkEmailAvailability = async (email) => {
    if (!email || !email.includes('@')) {
      setEmailError('');
      return;
    }

    setCheckingEmail(true);
    setEmailError('');

    try {
      // Try to check if user exists by attempting to get user attributes
      // This is a workaround since Cognito doesn't have a direct "check if email exists" API
      const { signIn } = await import('aws-amplify/auth');
      
      // We'll use a timeout to debounce the check
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // For now, we'll just clear the error
      // The actual check will happen on submit
      setEmailError('');
    } catch (err) {
      // Error checking is fine - means we can proceed
      setEmailError('');
    } finally {
      setCheckingEmail(false);
    }
  };

  // Debounced email check
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.email) {
        checkEmailAvailability(formData.email);
      }
    }, 1000); // Wait 1 second after user stops typing

    return () => clearTimeout(timer);
  }, [formData.email]);

  // Check if step is complete
  const isStep1Complete = formData.email && formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
  const isStep2Complete = formData.parentName && formData.parentEmail && formData.parentPhone;
  const isStep3Complete = formData.studentName && formData.examType;

  // Auto-advance to next step
  React.useEffect(() => {
    if (isStep1Complete && activeStep === 1) {
      setActiveStep(2);
    } else if (isStep2Complete && activeStep === 2) {
      setActiveStep(3);
    }
  }, [isStep1Complete, isStep2Complete, activeStep]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Comprehensive validation before submitting to Cognito
    const validationErrors = [];

    // Email validation
    if (!formData.email || !formData.email.includes('@')) {
      validationErrors.push('Valid email address is required');
    }

    // Password validation
    if (!formData.password || formData.password.length < 8) {
      validationErrors.push('Password must be at least 8 characters long');
    }

    if (formData.password !== formData.confirmPassword) {
      validationErrors.push('Passwords do not match');
    }

    // Check password complexity
    const hasUppercase = /[A-Z]/.test(formData.password);
    const hasLowercase = /[a-z]/.test(formData.password);
    const hasNumber = /[0-9]/.test(formData.password);

    if (!hasUppercase || !hasLowercase || !hasNumber) {
      validationErrors.push('Password must contain uppercase letter, lowercase letter, and number');
    }

    // Parent/Guardian validation
    if (!formData.parentName || formData.parentName.trim().length < 2) {
      validationErrors.push('Parent/Guardian full name is required (minimum 2 characters)');
    }

    if (!formData.parentEmail || !formData.parentEmail.includes('@')) {
      validationErrors.push('Valid parent/guardian email is required');
    }

    if (!formData.parentPhone || formData.parentPhone.trim().length < 8) {
      validationErrors.push('Valid contact phone number is required (minimum 8 digits)');
    }

    // Student validation
    if (!formData.studentName || formData.studentName.trim().length < 2) {
      validationErrors.push('Student full name is required (minimum 2 characters)');
    }

    // Exam type validation
    if (!formData.examType) {
      validationErrors.push('Please select which test the student is preparing for (Selective or OC)');
    }

    // If there are validation errors, show them and don't submit
    if (validationErrors.length > 0) {
      const errorList = validationErrors.map((err, idx) => `• ${err}`).join('\n');
      setError(`Please fix the following:\n\n${errorList}`);
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    console.log('Validation passed, submitting to Cognito...');

    try {
      // Import Amplify v6 Auth
      const { signUp } = await import('aws-amplify/auth');
      
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            name: formData.studentName,
            family_name: formData.parentName,
            'custom:parent_email': formData.parentEmail,
            'custom:parent_phone': formData.parentPhone,
            'custom:exam_year': formData.examYear,
            'custom:exam_type': formData.examType
          }
        }
      });

      console.log('Registration successful:', { isSignUpComplete, userId, nextStep });
      
      // Check if email verification is needed
      if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        setNeedsVerification(true);
        setLoading(false);
      } else {
        setSuccess(true);
        const timer = setTimeout(() => {
          navigate('/');
        }, 60000);
        setRedirectTimer(timer);
      }
    } catch (err) {
      console.error('Registration error:', err);
      
      // Better error messages
      if (err.name === 'UsernameExistsException' || err.message?.includes('already exists')) {
        // User exists - redirect to login with message
        setError(
          <div>
            <strong style={{ fontSize: '16px', display: 'block', marginBottom: '10px' }}>
              Account Already Exists
            </strong>
            <p style={{ marginBottom: '15px', lineHeight: '1.6' }}>
              An account with this email already exists. Please login instead.
            </p>
            <p style={{ marginBottom: '15px', lineHeight: '1.6', fontSize: '14px', color: '#666' }}>
              💡 If you haven't verified your email yet, you'll be able to enter your verification code on the login page.
            </p>
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: '12px 24px',
                background: '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '16px',
                width: '100%'
              }}
            >
              Go to Login
            </button>
          </div>
        );
        setEmailError('This email is already registered');
      } else if (err.name === 'InvalidPasswordException') {
        setError('Password does not meet requirements. Must be at least 8 characters with uppercase, lowercase, and numbers.');
      } else if (err.name === 'InvalidParameterException') {
        setError('Please check all fields are filled correctly. Make sure your password has uppercase, lowercase, and numbers.');
      } else {
        setError(err.message || 'Registration failed. Please check all fields and try again.');
      }
      
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setVerifyingCode(true);

    try {
      const { confirmSignUp } = await import('aws-amplify/auth');
      
      await confirmSignUp({
        username: formData.email,
        confirmationCode: verificationCode
      });

      console.log('Email verified successfully');
      
      // Track registration conversion for Google Ads
      const { trackRegistration } = await import('../utils/analytics');
      trackRegistration(formData.email);
      
      setNeedsVerification(false);
      setSuccess(true);
      const timer = setTimeout(() => {
        navigate('/login');
      }, 60000);
      setRedirectTimer(timer);
    } catch (err) {
      console.error('Verification error:', err);
      if (err.name === 'CodeMismatchException') {
        setError('Invalid verification code. Please check the code and try again.');
      } else if (err.name === 'ExpiredCodeException') {
        setError('Verification code has expired. Please request a new code.');
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setVerifyingCode(false);
    }
  };

  if (needsVerification) {
    return (
      <div className="card" style={{ maxWidth: '600px', margin: '40px auto', padding: '40px' }}>
        <div style={{ fontSize: '64px', marginBottom: '20px', textAlign: 'center' }}>📧</div>
        <h2 style={{ color: '#4f46e5', marginBottom: '20px', textAlign: 'center' }}>Check Your Email</h2>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px', textAlign: 'center', lineHeight: '1.6' }}>
          We've sent a verification code to <strong>{formData.email}</strong>
        </p>
        
        {error && (
          <div style={{
            padding: '15px',
            background: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleVerifyCode}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              Verification Code *
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              required
              maxLength={6}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '24px',
                textAlign: 'center',
                letterSpacing: '8px',
                fontWeight: 'bold'
              }}
              placeholder="000000"
            />
            <small style={{ color: '#666', fontSize: '13px', display: 'block', marginTop: '5px' }}>
              💡 Enter the 6-digit code from your email
            </small>
          </div>

          <button
            type="submit"
            disabled={verifyingCode || verificationCode.length !== 6}
            className="btn-primary"
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '18px',
              fontWeight: 'bold',
              opacity: (verifyingCode || verificationCode.length !== 6) ? 0.6 : 1,
              cursor: (verifyingCode || verificationCode.length !== 6) ? 'not-allowed' : 'pointer'
            }}
          >
            {verifyingCode ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <div style={{ 
          background: '#f0f9ff', 
          border: '1px solid #bfdbfe',
          borderRadius: '8px',
          padding: '15px',
          marginTop: '20px'
        }}>
          <p style={{ fontSize: '14px', color: '#1e40af', margin: 0 }}>
            <strong>Didn't receive the code?</strong><br/>
            Check your spam folder or contact us at <a href="mailto:prepsmart.help@gmail.com" style={{ color: '#4f46e5' }}>prepsmart.help@gmail.com</a>
          </p>
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '10px', marginBottom: 0 }}>
            💡 Verification emails are sent from <strong>noreply@prepsmart.com.au</strong>
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="card" style={{ maxWidth: '600px', margin: '40px auto', textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
        <h2 style={{ color: '#10b981', marginBottom: '20px' }}>Email Verified Successfully!</h2>
        <p style={{ fontSize: '18px', color: '#333', marginBottom: '15px', fontWeight: '600' }}>
          Your account is now active!
        </p>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
          You can now login with your email: <strong>{formData.email}</strong>
        </p>
        <div style={{ 
          background: '#f0f9ff', 
          border: '1px solid #bfdbfe',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          textAlign: 'left'
        }}>
          <p style={{ fontSize: '14px', color: '#1e40af', marginBottom: '10px', fontWeight: '600' }}>
            🎉 What You Can Do Now:
          </p>
          <ul style={{ fontSize: '14px', color: '#374151', lineHeight: '1.8', paddingLeft: '20px', margin: 0 }}>
            <li>Login with your email and password</li>
            <li>Access 3 free practice tests per subject</li>
            <li>Access 4 free topic-based tests across all subjects</li>
            <li>View detailed performance analytics</li>
            <li>Track your progress over time</li>
          </ul>
        </div>
        <div style={{ 
          background: '#d1fae5', 
          border: '1px solid #10b981',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px'
        }}>
          <p style={{ fontSize: '14px', color: '#065f46', margin: 0 }}>
            <strong>✨ Ready to start!</strong> Click the Login button below to access your account.
          </p>
        </div>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
          Need help? Contact us at <strong>prepsmart.help@gmail.com</strong>
        </p>
        <p style={{ fontSize: '12px', color: '#999', marginBottom: '20px' }}>
          💡 All system emails are sent from <strong>noreply@prepsmart.com.au</strong>
        </p>
        
        <div style={{ 
          background: '#f3f4f6', 
          borderRadius: '8px', 
          padding: '20px',
          marginBottom: '20px'
        }}>
          <p style={{ color: '#666', fontSize: '16px', marginBottom: '10px' }}>
            {isPaused ? 'Auto-redirect paused' : 'Redirecting to home page in'}
          </p>
          <div style={{ 
            fontSize: '48px', 
            fontWeight: 'bold', 
            color: countdown <= 10 ? '#ef4444' : '#4f46e5',
            marginBottom: '10px'
          }}>
            {countdown}s
          </div>
          <button
            onClick={() => setIsPaused(!isPaused)}
            className="btn-secondary"
            style={{ padding: '8px 20px', fontSize: '14px' }}
          >
            {isPaused ? '▶️ Resume' : '⏸️ Pause'}
          </button>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary"
            style={{ padding: '12px 32px' }}
          >
            Go to Login
          </button>
          <button
            onClick={() => navigate('/')}
            className="btn-secondary"
            style={{ padding: '12px 32px' }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '700px', margin: '40px auto' }}>
      <div className="card" style={{ padding: '40px' }}>
        <h1 style={{ fontSize: '36px', marginBottom: '10px', color: '#4f46e5', textAlign: 'center' }}>
          Create Your Account
        </h1>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px' }}>
          Join PrepSmart and start practicing for your {formData.examType === 'oc' ? 'OC placement' : formData.examType === 'selective' ? 'selective' : 'placement'} test
        </p>

        {error && (
          <div style={{
            padding: '15px',
            background: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '20px',
            whiteSpace: 'pre-line'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Login Details */}
          <div style={{ 
            marginBottom: '30px', 
            padding: '20px', 
            background: activeStep === 1 ? '#f0f9ff' : '#f9fafb', 
            borderRadius: '8px', 
            border: activeStep === 1 ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ 
                width: '30px', 
                height: '30px', 
                borderRadius: '50%', 
                background: isStep1Complete ? '#10b981' : activeStep === 1 ? '#3b82f6' : '#d1d5db',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                marginRight: '10px'
              }}>
                {isStep1Complete ? '✓' : '1'}
              </div>
              <h3 style={{ fontSize: '20px', margin: 0, color: activeStep === 1 ? '#4f46e5' : '#6b7280' }}>
                Create Your Login Details
              </h3>
            </div>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px', marginLeft: '40px' }}>
              These details will be used to log into the PrepSmart website
            </p>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
                placeholder="your.email@example.com"
              />
              {checkingEmail && (
                <small style={{ color: '#3b82f6', fontSize: '13px', display: 'block', marginTop: '5px' }}>
                  ⏳ Checking email availability...
                </small>
              )}
              {!checkingEmail && emailError && (
                <small style={{ color: '#dc2626', fontSize: '13px', display: 'block', marginTop: '5px', fontWeight: 'bold' }}>
                  ❌ {emailError}
                </small>
              )}
              {!checkingEmail && !emailError && formData.email && formData.email.includes('@') && (
                <small style={{ color: '#10b981', fontSize: '13px', display: 'block', marginTop: '5px', lineHeight: '1.5' }}>
                  ✅ Verification email will be sent to this address
                </small>
              )}
              {!formData.email && (
                <small style={{ color: '#666', fontSize: '13px', display: 'block', marginTop: '5px', lineHeight: '1.5' }}>
                  💡 <strong>Tip:</strong> Each child needs a unique email. Use parent+child1@gmail.com, parent+child2@gmail.com, etc.<br/>
                  📧 Verification email will be sent to this address.
                </small>
              )}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Create a Password *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
                placeholder="Minimum 8 characters"
              />
              <small style={{ color: '#666', fontSize: '13px', display: 'block', marginTop: '5px' }}>
                💡 Must contain uppercase letter, lowercase letter, and numbers (e.g., MyPass123)
              </small>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Confirm Password *
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
                placeholder="Re-enter your password"
              />
              {!passwordMatch && formData.confirmPassword && (
                <small style={{ color: '#dc2626', fontSize: '13px', display: 'block', marginTop: '5px', fontWeight: 'bold' }}>
                  ❌ Passwords do not match
                </small>
              )}
              {passwordMatch && formData.confirmPassword && (
                <small style={{ color: '#10b981', fontSize: '13px', display: 'block', marginTop: '5px', fontWeight: 'bold' }}>
                  ✅ Passwords match
                </small>
              )}
              {!formData.confirmPassword && (
                <small style={{ color: '#666', fontSize: '13px', display: 'block', marginTop: '5px' }}>
                  💡 Type the same password again to confirm
                </small>
              )}
            </div>
          </div>

          {/* Parent/Guardian Information */}
          <div style={{ 
            marginBottom: '30px',
            padding: '20px', 
            background: activeStep === 2 ? '#f0f9ff' : '#f9fafb', 
            borderRadius: '8px', 
            border: activeStep === 2 ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ 
                width: '30px', 
                height: '30px', 
                borderRadius: '50%', 
                background: isStep2Complete ? '#10b981' : activeStep === 2 ? '#3b82f6' : '#d1d5db',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                marginRight: '10px'
              }}>
                {isStep2Complete ? '✓' : '2'}
              </div>
              <h3 style={{ fontSize: '20px', margin: 0, color: activeStep === 2 ? '#4f46e5' : '#6b7280' }}>
                Parent/Guardian Details
              </h3>
            </div>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px', marginLeft: '40px' }}>
              Tell us about the parent or guardian managing this account
            </p>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Parent/Guardian Full Name *
              </label>
              <input
                type="text"
                name="parentName"
                value={formData.parentName}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
                placeholder="e.g., John Smith"
              />
              <small style={{ color: '#666', fontSize: '13px', display: 'block', marginTop: '5px' }}>
                💡 First and last name of parent or guardian
              </small>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Parent/Guardian Email *
              </label>
              <input
                type="email"
                name="parentEmail"
                value={formData.parentEmail}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
                placeholder="parent@example.com"
              />
              <small style={{ color: '#666', fontSize: '13px', display: 'block', marginTop: '5px' }}>
                💡 For updates and notifications (can be same as login email above)
              </small>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Contact Phone *
              </label>
              <input
                type="tel"
                name="parentPhone"
                value={formData.parentPhone}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
                placeholder="04XX XXX XXX"
              />
              <small style={{ color: '#666', fontSize: '13px', display: 'block', marginTop: '5px' }}>
                💡 Australian mobile number for important updates
              </small>
            </div>
          </div>

          {/* Student Information */}
          <div style={{ 
            marginBottom: '30px',
            padding: '20px', 
            background: activeStep === 3 ? '#f0f9ff' : '#f9fafb', 
            borderRadius: '8px', 
            border: activeStep === 3 ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ 
                width: '30px', 
                height: '30px', 
                borderRadius: '50%', 
                background: isStep3Complete ? '#10b981' : activeStep === 3 ? '#3b82f6' : '#d1d5db',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                marginRight: '10px'
              }}>
                {isStep3Complete ? '✓' : '3'}
              </div>
              <h3 style={{ fontSize: '20px', margin: 0, color: activeStep === 3 ? '#4f46e5' : '#6b7280' }}>
                Student Details
              </h3>
            </div>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px', marginLeft: '40px' }}>
              Tell us about the student who will be taking the practice tests
            </p>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Student's Full Name *
              </label>
              <input
                type="text"
                name="studentName"
                value={formData.studentName}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
                placeholder="e.g., Sarah Smith"
              />
              <small style={{ color: '#666', fontSize: '13px', display: 'block', marginTop: '5px' }}>
                💡 First and last name of the student taking the tests
              </small>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Which Test is the Student Preparing For? *
              </label>
              <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
                <label style={{
                  flex: 1,
                  padding: '15px',
                  border: formData.examType === 'selective' ? '2px solid #4f46e5' : '2px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: formData.examType === 'selective' ? '#eef2ff' : 'white',
                  transition: 'all 0.2s',
                  textAlign: 'center'
                }}>
                  <input
                    type="radio"
                    name="examType"
                    value="selective"
                    checked={formData.examType === 'selective'}
                    onChange={handleChange}
                    style={{ display: 'none' }}
                  />
                  <div style={{ fontSize: '20px', marginBottom: '6px' }}>🏫</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: formData.examType === 'selective' ? '#4f46e5' : '#6b7280', marginBottom: '4px' }}>
                    Selective High School
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    Year 6 → Year 7<br/>Reading, Maths, Thinking, Writing
                  </div>
                </label>
                <label style={{
                  flex: 1,
                  padding: '15px',
                  border: formData.examType === 'oc' ? '2px solid #4f46e5' : '2px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: formData.examType === 'oc' ? '#eef2ff' : 'white',
                  transition: 'all 0.2s',
                  textAlign: 'center'
                }}>
                  <input
                    type="radio"
                    name="examType"
                    value="oc"
                    checked={formData.examType === 'oc'}
                    onChange={handleChange}
                    style={{ display: 'none' }}
                  />
                  <div style={{ fontSize: '20px', marginBottom: '6px' }}>📚</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: formData.examType === 'oc' ? '#4f46e5' : '#6b7280', marginBottom: '4px' }}>
                    Opportunity Class (OC)
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    Year 4 → Year 5/6<br/>Reading, Maths, Thinking Skills
                  </div>
                </label>
              </div>
              <small style={{ color: '#666', fontSize: '13px', display: 'block', marginTop: '8px' }}>
                💡 OC is for Year 4 students entering Year 5/6. Selective is for Year 6 students entering Year 7. Please select one.
              </small>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Which Exam Year? *
              </label>
              <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
                <label style={{
                  flex: 1,
                  padding: '15px',
                  border: formData.examYear === '2027' ? '2px solid #4f46e5' : '2px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: formData.examYear === '2027' ? '#eef2ff' : 'white',
                  transition: 'all 0.2s',
                  textAlign: 'center'
                }}>
                  <input
                    type="radio"
                    name="examYear"
                    value="2027"
                    checked={formData.examYear === '2027'}
                    onChange={handleChange}
                    style={{ display: 'none' }}
                  />
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: formData.examYear === '2027' ? '#4f46e5' : '#6b7280', marginBottom: '5px' }}>
                    2027
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    May 2027
                  </div>
                </label>
              </div>
              <small style={{ color: '#666', fontSize: '13px', display: 'block', marginTop: '8px' }}>
                💡 Select the year your student will be taking the NSW {formData.examType === 'oc' ? 'OC Placement' : formData.examType === 'selective' ? 'Selective Schools' : 'Placement'} Test
              </small>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '18px',
              fontWeight: 'bold',
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <p style={{ textAlign: 'center', marginTop: '20px', color: '#666' }}>
            Already have an account?{' '}
            <a href="/login" style={{ color: '#4f46e5', fontWeight: 'bold', textDecoration: 'none' }}>
              Login here
            </a>
          </p>
        </form>
      </div>

      <div className="card" style={{ marginTop: '20px', padding: '20px', background: '#f0f9ff', borderLeft: '4px solid #3b82f6' }}>
        <p style={{ fontSize: '14px', color: '#1e40af', margin: 0 }}>
          <strong>✨ Instant Access:</strong> Once you verify your email, you'll have immediate access to all free practice tests. No waiting for approval! Fields marked with * are required.
        </p>
      </div>

      <div className="card" style={{ marginTop: '15px', padding: '20px', background: '#fef3c7', borderLeft: '4px solid #f59e0b' }}>
        <p style={{ fontSize: '14px', color: '#92400e', margin: '0 0 10px 0', fontWeight: 'bold' }}>
          👨‍👩‍👧‍👦 Multiple Children? Each Child Needs a Different Login Email
        </p>
        <p style={{ fontSize: '13px', color: '#92400e', margin: 0, lineHeight: '1.6' }}>
          <strong>Important:</strong> Each child must have their own unique login email to track their individual progress.<br/>
          <br/>
          <strong>Easy solution - Use email aliases:</strong><br/>
          • Child 1: parent@gmail.com<br/>
          • Child 2: parent+sarah@gmail.com<br/>
          • Child 3: parent+john@gmail.com<br/>
          <br/>
          ✅ All emails arrive in the same parent@gmail.com inbox<br/>
          ✅ Each child has separate login and progress tracking<br/>
          ✅ Works with Gmail, Outlook, Yahoo, and most providers
        </p>
      </div>

      <div className="card" style={{ marginTop: '15px', padding: '20px', background: '#f0f9ff' }}>
        <p style={{ fontSize: '14px', color: '#666', margin: 0, textAlign: 'center' }}>
          🔒 Your information is secure and will only be used to provide you with the best learning experience.
        </p>
      </div>
    </div>
  );
}

export default Register;
